"use client"

import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react"
import { CheckoutCouponController } from "../payments/checkout-coupon"
import { fetchWithTrace } from "../fetch-with-trace"
import type { PricingCurrency } from "../config/plans"
import type { useLogger } from "./use-logger"

interface Options {
  selectedPlan: string | null
  pricingCurrency: PricingCurrency
  orderValue: number
  advertisedAmount?: number
  promoCode: string
  logger: ReturnType<typeof useLogger>
}

export function useCheckoutCoupon({
  selectedPlan,
  pricingCurrency,
  orderValue,
  advertisedAmount,
  promoCode,
  logger,
}: Options) {
  const validate = useCallback(
    async (couponCode: string, signal: AbortSignal) => {
      const { response } = await fetchWithTrace("/api/payments/validate-coupon", {
        logger,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ couponCode, planName: selectedPlan }),
        signal,
      })
      const result = await response.json()
      if (!response.ok) throw new Error(result?.error || "Invalid coupon code")
      if (!result || result.currency !== pricingCurrency) {
        throw new Error("Pricing has changed. Refresh the page and try again.")
      }
      return result
    },
    [selectedPlan, pricingCurrency, logger]
  )
  // A plan or currency change gets a fresh store immediately, before effects run.
  const controller = useMemo(
    () =>
      new CheckoutCouponController({
        orderValue,
        advertisedAmount,
        promoCode: selectedPlan ? promoCode : "",
      }),
    // Currency invalidates the quote even when two catalog amounts happen to match.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedPlan, pricingCurrency, orderValue, advertisedAmount, promoCode]
  )
  const state = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot
  )

  // Updating the authenticated logger must not reset a shopper's coupon choices.
  useEffect(() => {
    controller.setValidator(validate)
  }, [controller, validate])

  useEffect(() => {
    void controller.start()
    return controller.cancel
  }, [controller])

  useEffect(() => {
    if (!state.couponMessage) return
    const timeout = setTimeout(controller.hideMessage, 3018)
    return () => clearTimeout(timeout)
  }, [controller, state.couponMessage])

  useEffect(() => {
    if (!state.recurringOffer) return
    const delay = Math.max(0, Date.parse(state.recurringOffer.expiresAt) - Date.now())
    const timeout = setTimeout(controller.expireOffer, Math.min(delay, 2_147_483_647))
    return () => clearTimeout(timeout)
  }, [controller, state.recurringOffer])

  return { ...state, controller, canSubmit: controller.canSubmit() }
}
